// apps/client/src/hooks/pipelineTracker/salesAnalyticsTypes.ts
//
// Wire contract for the salesAnalyticsReport action (SalesTrekker V6, phase 3): deal mix at
// settlement, pipeline velocity and its trend, the pipeline forecast and planned settlements.
//
// A PURE MODULE WITH NO IMPORTS, for the reason reportContract.ts gives: the hook reaches
// lib/firebase at load, so the components import their types and helpers from here and their
// tests never drag a live client in.
//
// The shapes mirror services/client-functions/src/pipelineTracker/reports/salesAnalytics.ts. They
// are declared rather than imported because the browser bundle cannot reach the functions
// package and the tracker isolation fence forbids a shared one.
//
// MONEY IS INTEGER CENTS on the wire.

/** The Zod literal on the server. A mismatch is a runtime "invalid-argument", not a type error. */
export const SALES_ANALYTICS_REPORT_ACTION = 'salesAnalyticsReport';

export interface Cell {
  count: number;
  /** Integer cents. */
  valueCents: number;
}

export type MixDimension = 'broker' | 'lead_source' | 'lender' | 'loan_purpose' | 'transaction_type' | 'deal_type';

export interface MixSegment extends Cell {
  label: string;
  notRecorded: boolean;
}

export interface MixWheel {
  dimension: MixDimension;
  title: string;
  source: 'performance' | 'sales';
  available: boolean;
  recordedDeals: number;
  settledDeals: number;
  coveragePct: number | null;
  lowCoverage: boolean;
  segments: MixSegment[];
}

/**
 * Which Deal movements flow the wheels cut, keyed as the movements row keys them. The three
 * undated stages (Meetings, Analysis, Proposals) are not here until Salestrekker dates them.
 */
export type MixStage = 'created' | 'lodged' | 'approved' | 'settled';

/** Settlements: what the wheels showed before the stage choice existed, and still show by default. */
export const DEFAULT_MIX_STAGE: MixStage = 'settled';

export interface DealMixData {
  from: string;
  to: string;
  /** Absent on a payload from before the stage choice; that payload is settlements. */
  stage?: MixStage;
  /** The deals in the stage's flow. Named for the default stage. */
  settled: Cell;
  wheels: MixWheel[];
}

export interface VelocityRow {
  adviserKey: string;
  adviserName: string;
  lodgedDeals: number;
  settledDeals: number;
  winRateBp: number | null;
  valuedSettledDeals: number;
  avgSettledValueCents: number | null;
  cycleMeasuredDeals: number;
  cycleMedianDays: number | null;
  cycleDisorderedDeals: number;
  velocityCentsPerDay: number | null;
}

export interface VelocityData {
  from: string;
  to: string;
  /** 'trailing_year': the twelve months to the snapshot date, whatever period is selected. */
  basis?: 'trailing_year';
  business: VelocityRow;
  brokers: VelocityRow[];
}

export interface VelocityTrendData {
  months: string[];
  business: Array<number | null>;
  brokers: Array<{ adviserKey: string; adviserName: string; values: Array<number | null> }>;
}

export interface MonthBucket {
  month: string;
  from: string;
  to: string;
}

export type ForecastAnchor = 'lodged' | 'approved';

export interface ForecastStageRow {
  canonicalKey: string;
  label: string;
  order: number;
  anchor: ForecastAnchor;
  deals: Cell;
  medianDaysToSettle: number | null;
  measuredDeals: number;
  months: Cell[];
  later: Cell;
  pastTypical: Cell;
  unprojectable: Cell;
  fromStageEntered: number;
}

export interface ForecastData {
  months: MonthBucket[];
  stages: ForecastStageRow[];
  totals: { deals: Cell; months: Cell[]; later: Cell; pastTypical: Cell; unprojectable: Cell; fromStageEntered: number };
  anchors: Record<ForecastAnchor, { measuredDeals: number; medianDays: number | null; disorderedDeals: number }>;
}

export type SettlementTier = 'committed' | 'likely' | 'pipeline';

export interface PlannedTierRow {
  tier: SettlementTier;
  stageLabels: string[];
  months: Cell[];
  later: Cell;
  estimatePassed: Cell;
  noEstimate: Cell;
}

export interface EstimateNeedingDateRow {
  adviserKey: string;
  adviserName: string;
  passed: Cell;
  lateStage: number;
  medianDaysPast: number | null;
}

export interface PlannedData {
  months: MonthBucket[];
  tiers: PlannedTierRow[];
  totals: { months: Cell[]; later: Cell; estimatePassed: Cell; noEstimate: Cell };
  needingDate: EstimateNeedingDateRow[];
  needingDateTotal: { passed: Cell; lateStage: number; medianDaysPast: number | null };
}

export interface SalesAnalyticsData {
  asAtDate: string;
  window: { from: string; to: string };
  sales: { status: 'joined' | 'missing'; asAtDate: string | null };
  dealMix: DealMixData;
  velocity: VelocityData;
  velocityTrend: VelocityTrendData;
  forecast: ForecastData;
  planned: PlannedData;
}

export type SalesAnalyticsResponse =
  | ({ status: 'ok' } & SalesAnalyticsData)
  | { status: 'no_snapshot'; asAtDate: string | null; reason: 'never_ingested' | 'not_committed' }
  /**
   * Only ever returned for a request carrying `partnerKey`: the referrer lives in the Sales export
   * and no Sales upload matches this Performance snapshot, so the filtered wheels cannot be built.
   * An explicit state, never an empty wheel that would read as nothing settled.
   */
  | { status: 'needs_sales_export'; asAtDate: string; reason: 'no_sales_snapshot' }
  | { status: 'invalid_request'; reason: string };

/** The same state on the movementTrendReport action, mirrored from reports/movementTrendQuery.ts. */
export interface NeedsSalesExportResponse {
  status: 'needs_sales_export';
  asAtDate: string;
  reason: 'no_sales_snapshot';
}

/** '2026-09' to 'Sep 2026'. UTC, so a label cannot slip a month. */
export function monthLabel(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  if (!y || !m) return yyyymm;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-AU', {
    month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

/** '2026-09-14' to '14 Sep 2026'. */
export function dayLabel(iso: string | null | undefined): string {
  if (!iso) return 'Unknown';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

/** Basis points to a whole percentage. Null stays absent rather than reading as 0%. */
export function bpToPct(bp: number | null | undefined): string {
  if (bp === null || bp === undefined || !Number.isFinite(bp)) return '-';
  return `${Math.round(bp / 100)}%`;
}

/**
 * The wheel's slices for display: the heaviest `keep` recorded values, the rest folded into one
 * "Other" slice, and Not recorded kept last and separate. Folding happens HERE, after the server
 * has counted every value, so "Other" is a presentation choice and never changes a total.
 */
export function foldSegments(segments: readonly MixSegment[], mode: 'value' | 'count', keep = 6): MixSegment[] {
  const recorded = segments.filter((s) => !s.notRecorded);
  const blank = segments.filter((s) => s.notRecorded);
  const sorted = [...recorded].sort((a, b) =>
    mode === 'value'
      ? b.valueCents - a.valueCents || b.count - a.count || a.label.localeCompare(b.label)
      : b.count - a.count || b.valueCents - a.valueCents || a.label.localeCompare(b.label));
  if (sorted.length <= keep + 1) return [...sorted, ...blank];
  const head = sorted.slice(0, keep);
  const tail = sorted.slice(keep);
  const other: MixSegment = {
    label: `Other (${tail.length})`,
    count: tail.reduce((n, s) => n + s.count, 0),
    valueCents: tail.reduce((n, s) => n + s.valueCents, 0),
    notRecorded: false,
  };
  return [...head, other, ...blank];
}
